import React, { useState, type ComponentProps } from 'react'
import type { AccountHolderAddress, AddressMonikerId } from '@mobi/api-types'
import { formatAddress, createFullSuburb } from '@mobi/utils'
import { AddressManualEntry, type ManualAddress } from './AddressManualEntry'
import { ManualInputTextStyled } from './AddressInput.styled'
import { ValidationResult } from '../../../../component-library/src/types'
import { Input } from '@mobi/component-library/Common/V2'
import {
  AutocompleteInputField,
  AutocompleteInputProps,
} from '@mobi/component-library/Common/Input/AutocompleteInputField/AutocompleteInputField'
import { getRequiredValidationMessage } from '@mobi/account/Areas/SignUp/Utils'

type FetchSuggestionsCallback = ComponentProps<
  typeof AutocompleteInputField
>['fetchSuggestionsCallback']

type EntryMode = 'Autocomplete' | 'Manual'

export type AddressInputProps = {
  prefix?: string
  address?: AccountHolderAddress
  defaultEntryMode?: EntryMode
  required?: boolean
  fetchAddressSuggestionsCallback: FetchSuggestionsCallback
  fetchStreetSuggestionsCallback: FetchSuggestionsCallback
  fetchSuburbSuggestionsCallback: FetchSuggestionsCallback
  onEntryModeChanged?: (mode: EntryMode) => void
  onValidationChange?: (result: ValidationResult<AddressMonikerId | ManualAddress>) => void
  isDisabled?: boolean
}

export const AddressInput = ({
  prefix = '',
  address,
  defaultEntryMode = 'Autocomplete',
  required = true,
  fetchAddressSuggestionsCallback,
  fetchStreetSuggestionsCallback,
  fetchSuburbSuggestionsCallback,
  onEntryModeChanged,
  onValidationChange,
  isDisabled = false,
}: AddressInputProps) => {
  const [entryMode, setEntryMode] = useState<EntryMode>(defaultEntryMode)
  const [autocompleteValidationMessage, setAutocompleteValidationMessage] = useState('')

  if (entryMode === 'Autocomplete') {
    return (
      <div>
        <AutocompleteInputField
          name='Address'
          id={`${prefix}address`}
          inputName={`${prefix}address`}
          placeholder='Enter address'
          autoComplete='street-address'
          defaultValue={
            address
              ? formatAddress({
                  unitNumber: address.unitNumber,
                  streetNumber: address.streetNumber,
                  streetName: address.streetName,
                  streetType: address.streetType,
                  suburbStateAndPostcode: createFullSuburb(address),
                })
              : ''
          }
          required={required}
          fetchSuggestionsCallback={fetchAddressSuggestionsCallback}
          onSelect={addressMonikerId =>
            onValidationChange?.({ isValid: true, result: addressMonikerId })
          }
          onUnselect={() => onValidationChange?.({ isValid: false })}
          renderInput={({
            inputName,
            name,
            ...props
          }: Omit<AutocompleteInputProps, 'defaultValue' | 'type'>) => (
            <Input
              {...props}
              name={inputName ?? 'autocomplete'}
              label={name}
              autoComplete='new-password'
              isDisabled={isDisabled}
              onFocus={() => setAutocompleteValidationMessage('')}
              onBlur={() => {
                setAutocompleteValidationMessage(getRequiredValidationMessage(props.value))
              }}
              isInvalid={!!autocompleteValidationMessage}
              helpText={autocompleteValidationMessage}
            />
          )}
        />
        <ManualInputTextStyled>
          Having trouble?{' '}
          <span
            role='button'
            tabIndex={0}
            onClick={event => {
              setEntryMode('Manual')
              onEntryModeChanged?.('Manual')
              event.preventDefault()
            }}
          >
            Enter your address manually
          </span>
        </ManualInputTextStyled>
      </div>
    )
  }

  return (
    <AddressManualEntry
      inputNamePrefix={prefix}
      fetchStreetSuggestionsCallback={fetchStreetSuggestionsCallback}
      fetchSuburbSuggestionsCallback={fetchSuburbSuggestionsCallback}
      onValidationChange={onValidationChange}
      isDisabled={isDisabled}
    />
  )
}
